import { Breadcrumb, Button, Dropdown, Select, Tag } from "antd";
import React, { useEffect, useState } from "react";
import Table, { ColumnType } from "antd/es/table";

import { CloseOutlined, SlidersOutlined } from "@ant-design/icons";

import { cn } from "../../../lib/utils";
import MyPagination from "../../components/Pagination";
import { IBucketPayloadType } from "@src/types/api/filling-management";
import { IUser } from "../../../types/api/user";
import {
  useFIllingManagementList,
  useSendReminder,
  useUpdateApprovalStatus,
} from "../../../hooks/api/filling-management/hooks";
import { useMessageApi } from "../../../provider/AntdNotification";
import { FillingManagementFilterMenu } from "../filling-management/FillingManagementFilter";
import { useNavigate } from "react-router-dom";

const BucketTable = ({
  allowURLStateUpdate = true,
}: {
  allowURLStateUpdate?: boolean;
}) => {
  const navigate = useNavigate();

  const [breadcrumb] = useState("Bucket");
  const messageApi = useMessageApi();
  const [query, setQuery] = useState({
  page: 1,
  limit: 20,
  isLowScore: true, 
});

  const {
    data,
    refetchWithQuery: refetch,
    isFetching,
  } = useFIllingManagementList(query);

  /** ---- 🎯 Store filters as single-value only ---- **/
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string>
  >({});

  /** ---- Remove selected filter ---- **/
const removeFilter = (key: string) => {
  setSelectedFilters((prev) => {
    const updated = { ...prev };
    delete updated[key];

    setQuery((prevQuery) => ({
      ...prevQuery,
      page: 1,
      ...updated,
    }));

    return updated;
  });
};

  

  const { mutate: updateApprovalStatus, isPending: isUpdatingStatus } =
    useUpdateApprovalStatus({
      onSuccess: () => {
        messageApi.success("Approval Status Updated Successfully");
        refetch(query);
      },
      onError: (err: any) => {
        messageApi.error(err.message || "Error in Updating Approval Status");
      },
    });

  const DEFAULT_NUQS_CONFIG = {
    throttleMs: !allowURLStateUpdate ? Infinity : 0,
  };

  const qData = data?.qData ?? { page: 1, limit: 20 };

  const columns: ColumnType<IBucketPayloadType>[] = [
    {
      title: "User ID",
      dataIndex: "user",
      render: (user: IUser) => <span>{user?.user_Id}</span>,
    },

    {
      title: "Name",
      dataIndex: "user",
      render: (user: IUser) => <span>{user?.name}</span>,
    },
    {
      title: "Filling Year",
      dataIndex: "filling_year",
      render: (year: string) => (
        <span className="text-gray-800">{year || "-"}</span>
      ),
    },
    {
      title: "Filling Type",
      dataIndex: "filling_type",
    },

    {
      title: "Status",
      dataIndex: "status",
      render: (_, row) => {
        const status = row.status ?? "DRAFT";

        const colorMap: Record<string, string> = {
          DRAFT: "bg-blue-100 text-blue-700",
          SUBMITTED: "bg-green-100 text-green-700",
          PAID: "bg-purple-100 text-purple-700",
        };

        const labelMap: Record<string, string> = {
          DRAFT: "In Progress",
          SUBMITTED: "Submitted",
          PAID: "Paid",
        };

        return (
          <Tag
            className={cn(
              "px-3 py-1 rounded-full text-xs font-semibold capitalize",
              colorMap[status],
            )}
          >
            {labelMap[status] ?? status}
          </Tag>
        );
      },
    },
    {
      title: "Approval Status",
      dataIndex: "approval",
      render: (approval: any, row: IBucketPayloadType) => {
        const status = approval?.status || "NOT_REQUIRED";

        return (
          <Select
            defaultValue={status}
            onChange={(newStatus) =>
              updateApprovalStatus({ bucketId: row._id!, status: newStatus })
            }
            loading={isUpdatingStatus}
            className="w-40"
            options={[
              { label: "Not Required", value: "NOT_REQUIRED" },
              { label: "Pending", value: "PENDING" },
              { label: "Approved", value: "APPROVED" },
            ]}
          />
        );
      },
    },

    {
      title: "Action",
      key: "action",
      align: "right",
      render: (_, record) => {
        return (
          <>
            <div className="flex items-center justify-end gap-3">
              <span
                onClick={() => navigate(`/filling-management/${record._id}`)}
                className=" cursor-pointer  text-primary"
              >
                View
              </span>
            </div>
          </>
        );
      },
    },
  ];
useEffect(() => {
  setQuery((prev) => ({
    ...prev,
    page: 1,
    ...selectedFilters,
  }));
}, [selectedFilters]);

useEffect(() => {
  refetch(query);
}, [query]);
  return (
    <div>
      <h1 className="text-lg sm:text-3xl font-semibold">Bucket </h1>

      <Breadcrumb
        separator=">"
        items={[{ title: "Dashboard" }, { title: breadcrumb }]}
        className="text-xs sm:text-base my-2"
      />

      {/* Filter Button */}
      <div className="flex justify-end mb-3">
        <Dropdown
          trigger={["click"]}
          placement="bottomRight"
          dropdownRender={() => (
            <FillingManagementFilterMenu
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
            />
          )}
        >
          <Button
            icon={<SlidersOutlined />}
            className="bg-[#FEF2E7] custom_shadow font-semibold"
          >
            Filters
          </Button>
        </Dropdown>
      </div>

      {/* Selected Filter Tags */}
      {Object.keys(selectedFilters).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(selectedFilters).map(([key, value]) => (
            <Tag
              key={key}
              closable
              color="green"
              onClose={() => removeFilter(key)}
              closeIcon={<CloseOutlined />}
            >
              {value}
            </Tag>
          ))}
        </div>
      )}

      {/* Table */}
      <Table
        dataSource={data?.data}
        columns={columns}
        rowKey={(item) => item._id ?? ""}
        loading={isFetching}
        pagination={false}
        scroll={{ x: true }}
        size="middle"
        className="custom-rounded-table"
        footer={() => (
          <MyPagination
            qData={qData}
            onChange={refetch}
            total={qData.total}
            DEFAULT_NUQS_CONFIG={DEFAULT_NUQS_CONFIG}
          />
        )}
      />
    </div>
  );
};

export default BucketTable;
